import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { paymentService } from "../services/paymentService";
import { CalendarIcon } from "@heroicons/react/24/outline";
import Pagination from "../../../components/common/Pagination";
import LoadingSpinner from "../../../components/common/LoadingSpinner";
import DatePicker from "../../../components/common/DatePicker";

const PaymentList = () => {
  const [page, setPage] = useState(1);
  const [paymentType, setPaymentType] = useState("all"); // 'all', 'pending', 'completed', 'failed'
  const [dateRange, setDateRange] = useState({
    startDate: null,
    endDate: null
  });
  const [showDatePicker, setShowDatePicker] = useState(false);
  
  const { data, isLoading, isError } = useQuery({
    queryKey: ["payments", page, paymentType, dateRange],
    queryFn: () => paymentService.getPayments({ 
      page, 
      status: paymentType,
      startDate: dateRange.startDate ? dateRange.startDate.toISOString().split('T')[0] : null,
      endDate: dateRange.endDate ? dateRange.endDate.toISOString().split('T')[0] : null
    }),
  });

  if (isLoading) return <LoadingSpinner />;
  if (isError)
    return (
      <div className="text-red-500">Erreur lors du chargement des paiements</div>
    );

  const payments = data?.data || [];
  const pagination = data?.meta || {};

  const formatPrice = (price) => {
    return new Intl.NumberFormat("fr-FR", {
      style: "currency",
      currency: "XOF",
    }).format(price);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };
  
  const handleDateRangeChange = (range) => {
    setDateRange(range);
    setShowDatePicker(false);
  };

  const clearDateFilter = () => {
    setDateRange({
      startDate: null,
      endDate: null
    });
  };
 
  return (
    <div className="bg-white shadow-md min-h-full rounded-lg">
      <div className="p-4 border-b border-gray-200 flex justify-between items-center">
        <h2 className="text-xl font-semibold text-gray-800">
          Historique des paiements
        </h2>
        
        <div className="flex items-center space-x-2">
          <button 
            onClick={() => setShowDatePicker(!showDatePicker)}
            className="px-3 py-2 flex items-center text-sm border border-gray-300 rounded hover:bg-gray-50"
          >
            <CalendarIcon className="h-4 w-4 mr-2" />
            {dateRange.startDate && dateRange.endDate 
              ? `${formatDate(dateRange.startDate)} - ${formatDate(dateRange.endDate)}`
              : "Sélectionner une période"
            }
          </button>
          
          {dateRange.startDate && dateRange.endDate && (
            <button 
              onClick={clearDateFilter}
              className="px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded"
            >
              Effacer
            </button>
          )}
        </div>
      </div>

      {/* Date Range Picker Dropdown */}
      {showDatePicker && (
        <div className="absolute right-4 mt-2 z-10 bg-white border rounded-lg shadow-lg p-4">
          <DatePicker 
            initialRange={dateRange}
            onRangeChange={handleDateRangeChange}
          />
        </div>
      )}

      {/* Payment Type Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex space-x-2 mb-3 pt-4 px-4" aria-label="Tabs">
          {[
            { label: "Tous", value: "all" },
            { label: "En attente", value: "pending" },
            { label: "Complétés", value: "completed" },
            { label: "Échoués", value: "failed" },
          ].map((tab) => (
            <button
              key={tab.value}
              onClick={() => setPaymentType(tab.value)}
              className={`px-4 py-2 text-sm font-medium transition cursor-pointer rounded ${
                paymentType === tab.value
                  ? "bg-primary text-white shadow"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-primary text-white">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase">
                ID Transaction
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase">
                Commande
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase">
                Montant
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase">
                Méthode
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase">
                Statut
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase">
                Date
              </th>
            </tr>
          </thead>

          {payments.length > 0 ? (
            <tbody className="bg-white divide-y divide-gray-200">
              {payments.map((payment) => (
                <tr key={payment.id}>
                  <td className="px-4 py-3 text-sm font-medium">
                    {payment.transaction_id}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    #{payment.order_id}
                  </td>
                  <td className="px-4 py-3 text-sm font-medium">
                    {formatPrice(payment.amount)}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    {payment.provider}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      payment.status === 'completed' ? 'bg-green-100 text-green-800' :
                      payment.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {payment.status === 'completed' ? 'Complété' :
                       payment.status === 'pending' ? 'En attente' : 'Échoué'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm">
                    {formatDate(payment.created_at)}
                  </td>
                </tr>
              ))}
            </tbody>
          ) : (
            <tbody>
              <tr>
                <td colSpan="6">
                  <div className="flex justify-center items-center py-10 text-gray-500 text-center text-sm">
                    Aucun paiement trouvé pour cette période.
                  </div>
                </td>
              </tr>
            </tbody>
          )}
        </table>
      </div>

      <Pagination
        currentPage={page}
        totalItems={pagination.total}
        itemsPerPage={pagination.per_page}
        onPageChange={setPage}
      />
    </div>
  );
};

export default PaymentList;